import { useCallback, useEffect, useRef } from 'react';
import { getUploadActions, useUploadStore } from '../store/uploadStore';
import { uploadService } from '../services/uploadService';
import { useNotifications } from '../../../ui';
import type { UploadItemFile, UploadCancelFunction } from '../types';

export interface UseItemUpload {
  // State
  state: {
    isUploading: boolean;
    progress: { loaded: number; total: number; percentage: number } | null;
    error: string | null;
    currentItem: UploadItemFile | null;
  };
  
  // Computed
  computed: {
    isDisabled: boolean;
    hasFile: boolean;
  };
  
  // Actions
  actions: {
    updateItem: (updates: Partial<UploadItemFile>) => void;
    setFile: (file: File | null) => void;
    setName: (name: string) => void;
    setOwner: (owner: string) => void;
    startUpload: () => Promise<void>;
    cancelUpload: () => void;
    resetUpload: () => void;
    clearError: () => void;
  };
}

export const useItemUpload = (): UseItemUpload => {
  const state = useUploadStore();
  const actions = getUploadActions();
  const { show } = useNotifications();
  const cancelFunctionRef = useRef<UploadCancelFunction | null>(null);

  useEffect(() => {}, []);

  // Get computed values directly from store to avoid hook dependency issues
  const isDisabled = !state.currentItem?.file || !state.currentItem?.name || state.isUploading;
  const hasFile = !!state.currentItem?.file;
  
  const computed = { isDisabled, hasFile };

  // Update item partially
  const updateItem = useCallback((updates: Partial<UploadItemFile>) => {
    const currentItem = state.currentItem;
    if (currentItem) {
      actions.setCurrentItem({
        ...currentItem,
        ...updates,
      });
    }
  }, [state.currentItem, actions, state.isUploading]);

  // Set file and auto-update name if empty
  const setFile = useCallback((file: File | null) => {
    const currentItem = state.currentItem;
    if (currentItem) {
      const updates: Partial<UploadItemFile> = {
        file: file || undefined,
      };
      
      // Auto-set name if empty and file is provided
      if (file && !currentItem.name.trim()) {
        updates.name = file.name;
      }
      
      actions.setCurrentItem({
        ...currentItem,
        ...updates,
      });
    }
    
    // Clear error when file changes
    if (state.error) {
      actions.setError(null);
    }
  }, [state.currentItem, state.error, actions]);

  // Set name
  const setName = useCallback((name: string) => {
    updateItem({ name });
  }, [updateItem]);

  // Set owner
  const setOwner = useCallback((owner: string) => {
    updateItem({ owner });
  }, [updateItem]);

  // Start upload
  const startUpload = useCallback(async () => {
    if (!state.currentItem || computed.isDisabled) {
      console.warn('Upload cannot be started');
      return;
    }

    actions.setError(null);
    actions.setUploading(true);
    actions.setProgress(null);

    try {
      const cancelFunction = await uploadService.uploadItem(state.currentItem, {
        onProgress: (progress) => {
          actions.setProgress(progress);
        },
        onSuccess: () => {
          // Upload completed successfully
          actions.setUploading(false);
          actions.setProgress(null);
          show('File uploaded successfully', 'success');
          actions.resetUpload(); // Reset the form
          cancelFunctionRef.current = null;
        },
        onError: (error) => {
          actions.setUploading(false);
          actions.setProgress(null);
          actions.setError(error);
          show(error, 'error');
          cancelFunctionRef.current = null;
        },
      });

      cancelFunctionRef.current = cancelFunction;

      if (!cancelFunction) {
        // Upload couldn't start (handled in onError callback)
        return;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      actions.setUploading(false);
      actions.setProgress(null);
      actions.setError(errorMessage);
      show(errorMessage, 'error');
      cancelFunctionRef.current = null;
    }
  }, [state.currentItem, computed.isDisabled, actions, show]);

  // Cancel upload
  const cancelUpload = useCallback(() => {
    if (cancelFunctionRef.current) {
      cancelFunctionRef.current();
      cancelFunctionRef.current = null;
    }
    
    actions.setUploading(false);
    actions.setProgress(null);
    actions.setError(null);
    show('Upload cancelled', 'warning');
  }, [actions, show]);

  // Reset upload
  const resetUpload = useCallback(() => {
    if (cancelFunctionRef.current) {
      cancelFunctionRef.current();
      cancelFunctionRef.current = null;
    }
    actions.resetUpload();
  }, [actions]);

  // Clear error
  const clearError = useCallback(() => {
    actions.setError(null);
  }, [actions]);

  return {
    state,
    computed,
    actions: {
      updateItem,
      setFile,
      setName,
      setOwner,
      startUpload,
      cancelUpload,
      resetUpload,
      clearError,
    },
  };
};